/**
 * localStorage persistence.
 *
 * Everything is written under one versioned key so a schema change is a single
 * migration point. All access is wrapped: Safari private mode throws on write,
 * and a corrupted value shouldn't take the whole app down — a failed load just
 * starts the user fresh rather than showing a blank screen.
 */

export const STORAGE_KEY = 'scripture-journey/state';
export const STORAGE_VERSION = 1;

function hasStorage() {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    // Accessing localStorage can itself throw when cookies are blocked.
    return false;
  }
}

/**
 * Merge a saved payload over the defaults one level deep, so fields added in a
 * later release appear with their default value instead of `undefined` for
 * users who already have saved state.
 */
function mergeWithDefaults(defaults, saved) {
  const out = { ...defaults, ...saved };
  for (const [key, fallback] of Object.entries(defaults)) {
    const isPlainObject =
      fallback && typeof fallback === 'object' && !Array.isArray(fallback);
    if (isPlainObject) {
      out[key] = { ...fallback, ...(saved?.[key] ?? {}) };
    }
  }
  return out;
}

export function loadState(defaults) {
  if (!hasStorage()) return { ...defaults };

  let raw;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return { ...defaults };
  }
  if (!raw) return { ...defaults };

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return { ...defaults };

    // Future migrations branch here on parsed.version.
    if (parsed.version !== STORAGE_VERSION) {
      return { ...mergeWithDefaults(defaults, parsed), version: STORAGE_VERSION };
    }
    return mergeWithDefaults(defaults, parsed);
  } catch {
    return { ...defaults };
  }
}

/** @returns {boolean} whether the write succeeded. */
export function saveState(state) {
  if (!hasStorage()) return false;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...state, version: STORAGE_VERSION }),
    );
    return true;
  } catch {
    // Quota exceeded or storage disabled — the session still works in memory.
    return false;
  }
}

export function clearState() {
  if (!hasStorage()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing useful to do */
  }
}
